import { isUserLoggedIn } from '../utils/is-logged-in.js';

function inferApiBase() {
  if (typeof window === 'undefined') {
    return 'http://localhost:3001';
  }
  const host = window.location?.hostname || 'localhost';
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  return (isLocal ? 'http://' + host + ':3001' : 'https://api.agilepro.ai');
}

export class ChatsGateway {
  constructor({ fetch, apiBase } = {}) {
    this.fetch = typeof fetch === 'function' ? fetch : (typeof window !== 'undefined' ? window.fetch.bind(window) : null);
    if (!this.fetch) {
      throw new Error('fetch implementation is required');
    }
    this.apiBase = apiBase || inferApiBase();
  }

  async listUnfiledChats({ limit = 50, offset = 0 } = {}) {
    if (!isUserLoggedIn()) {
      return [];
    }
    const url = this.apiBase + '/api/chats?teamId=NULL&status=active&limit=' + limit + '&offset=' + offset;
    const res = await this.fetch(url, { credentials: 'include' });
    return this.#parseResponse(res);
  }

  async listTeamChats(teamId, { limit = 50, offset = 0 } = {}) {
    if (!isUserLoggedIn() || !teamId) return [];
    const url = this.apiBase + '/api/chats?teamId=' + encodeURIComponent(teamId) + '&status=active&limit=' + limit + '&offset=' + offset;
    const res = await this.fetch(url, { credentials: 'include' });
    return this.#parseResponse(res);
  }


  async moveChat({ chatId, teamId } = {}) {
    if (!chatId) {
      throw new Error('chatId is required');
    }
    const url = this.apiBase + '/api/chats/' + encodeURIComponent(chatId);
    const res = await this.fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ teamId }),
    });
    return this.#parseResponse(res);
  }

  async renameChat({ chatId, title } = {}) {
    if (!chatId) {
      throw new Error('chatId is required');
    }
    const url = this.apiBase + '/api/chats/' + encodeURIComponent(chatId);
    const res = await this.fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    return this.#parseResponse(res);
  }

  async togglePinChat({ chatId, isPinned } = {}) {
    if (!chatId) {
      throw new Error('chatId is required');
    }
    const url = this.apiBase + '/api/chats/' + encodeURIComponent(chatId);
    const res = await this.fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isPinned }),
    });
    return this.#parseResponse(res);
  }

  async deleteChat(chatId) {
    if (!chatId) {
      throw new Error('chatId is required');
    }
    const url = this.apiBase + '/api/chats/' + encodeURIComponent(chatId);
    const res = await this.fetch(url, {
      method: 'DELETE',
      credentials: 'include',
    });
    return this.#parseResponse(res, true);
  }

  async loadChat(chatId) {
    if (!chatId) {
      throw new Error('chatId is required');
    }
    const url = this.apiBase + '/api/chats/' + encodeURIComponent(chatId);
    const res = await this.fetch(url, { credentials: 'include' });
    return this.#parseResponse(res);
  }

  async #parseResponse(response, allowEmpty = false) {
    if (!response || !response.ok) {
      const message = response ? 'HTTP ' + response.status : 'Network error';
      throw new Error(message);
    }
    const contentType = response.headers && typeof response.headers.get === 'function' ? response.headers.get('content-type') : null;
    if (allowEmpty && (!contentType || contentType.indexOf('json') === -1)) {
      return null;
    }
    if (contentType && contentType.indexOf('json') !== -1) {
      return response.json();
    }
    return response.text();
  }
}

if (typeof window !== 'undefined') {
  window.AgileProGateways = window.AgileProGateways || {};
  window.AgileProGateways.ChatsGateway = ChatsGateway;
}
